// Academic Search Provider types
export type AcademicSearchProvider =
  | "pubmed"
  | "semantic-scholar"
  | "google-scholar"
  | "arxiv"
  | "eric"
  | "europe-pmc"
  | "openalex";

export const AcademicSearchProviderValues: readonly AcademicSearchProvider[] = [
  "pubmed",
  "semantic-scholar",
  "google-scholar",
  "arxiv",
  "eric",
  "europe-pmc",
  "openalex",
] as const;

// Academic Search Author interface
export interface AcademicSearchAuthor {
  name: string;
  url?: string;
}

// Academic Search Base publication interface
export interface AcademicSearchBasePublication {
  doi?: string;
  title?: string;
  url?: string;
  providerURL?: string;
  authors?: AcademicSearchAuthor[];
  date?: string;
  year?: number;
  totalCitations?: number;
  totalReferences?: number;
  abstract?: string;
  pdfURL?: string;
  provider?: AcademicSearchProvider;
}

// Academic Search Semantic Scholar provider data interface
export interface AcademicSearchSemanticScholarData {
  paperId: string;
  venue?: string;
  publicationTypes?: string[];
  influentialCitationCount?: number;
  fieldsOfStudy?: string[];
  s2FieldsOfStudy?: {
    category: string;
    source: string;
  }[];
  openAccessPdfStatus?:
    | "GREEN"
    | "GOLD"
    | "HYBRID"
    | "BRONZE"
    | "CLOSED"
    | null;
  externalIds?: {
    CorpusId?: number;
    PubMed?: string;
    ArXiv?: string;
    MAG?: string;
    ACL?: string;
    PubMedCentral?: string;
    DBLP?: string;
  };
  isOpenAccess?: boolean;
  citationVelocity?: number;
  publicationVenue?: {
    id?: string;
    name?: string;
    type?: string;
    url?: string;
    alternate_names?: string[];
    issn?: string;
  };
}

// Academic Search Google Scholar provider data interface
export interface AcademicSearchGoogleScholarData {
  scholarId: string;
  displayedLink: string;
  snippet?: string;
  citedByLink?: string;
  citedByCount?: string;
  versionsLink?: string;
  versionsCount?: string;
  relatedPagesLink?: string;
  resources?: {
    title: string;
    type: string;
    link: string;
  }[];
}

// Academic Search PubMed provider data interface
export interface AcademicSearchPubMedData {
  pmid: string;
  pmcid?: string;
  journal?: string;
  journalAbbreviation?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  meshTerms?: string[];
  keywords?: string[];
  publicationTypes?: string[];
  authorAffiliations?: {
    author: string;
    affiliation: string;
  }[];
  publicationStatus?: string;
  language?: string;
  grantNumbers?: string[];
  conflictOfInterest?: string;
  chemicalList?: string[];
  dataAvailability?: string;
  history?: {
    received?: string;
    accepted?: string;
    revised?: string;
  };
}

// Academic Search ArXiv provider data interface
export interface AcademicSearchArxivData {
  arxivId: string;
  version?: string;
  categories?: string[];
  comment?: string;
  journalRef?: string;
  primaryCategory?: string;
  submittedDate?: string;
  updatedDate?: string;
  license?: string;
  announceType?: "new" | "replace" | "cross-list";
}

// Academic Search ERIC provider data interface
export interface AcademicSearchEricData {
  ericId: string;
  source?: string;
  publicationType?: string;
  educationLevel?: string[];
  audience?: string[];
  institution?: string;
  sponsor?: string;
  isbn?: string;
  issn?: string;
  descriptors?: string[];
  publisher?: string;
  peerReviewed?: boolean;
  fullTextAvailable?: boolean;
}

// Academic Search Europe PMC provider data interface
export interface AcademicSearchEuropePmcData {
  europePmcId: string;
  source?: string;
  pmid?: string;
  pmcid?: string;
  doi?: string;
  publicationType?: string;
  journalInfo?: {
    journal?: string;
    issn?: string;
    volume?: string;
    issue?: string;
  };
  grantsList?: {
    agency?: string;
    grantId?: string;
  }[];
  meshHeadingList?: string[];
  keywordList?: string[];
  chemicalList?: string[];
  authorAffiliationsList?: string[];
  fullTextAvailable?: boolean;
  isOpenAccess?: boolean;
  inEPMC?: boolean;
  inPMC?: boolean;
  hasPDF?: boolean;
  hasBook?: boolean;
  hasSuppl?: boolean;
  citedByCount?: number;
}

// Academic Search OpenAlex provider data interface
export interface AcademicSearchOpenAlexData {
  openAlexId: string;
  doi?: string;
  displayName?: string;
  publicationYear?: number;
  publicationDate?: string;
  primaryLocation?: {
    source?: {
      id?: string;
      displayName?: string;
      issn?: string[];
      type?: string;
      hostOrganization?: string;
      hostOrganizationName?: string;
    };
    license?: string;
    version?: string;
    isOa?: boolean;
  };
  openAccess?: {
    isOa?: boolean;
    oaStatus?: string;
    oaUrl?: string;
    anyRepositoryHasFulltext?: boolean;
  };
  authorships?: {
    authorPosition?: string;
    author?: {
      id?: string;
      displayName?: string;
      orcid?: string;
    };
    institutions?: {
      id?: string;
      displayName?: string;
      ror?: string;
      countryCode?: string;
      type?: string;
    }[];
    countries?: string[];
    isCorresponding?: boolean;
    rawAffiliationStrings?: string[];
  }[];
  countsByYear?: {
    year: number;
    citedByCount: number;
  }[];
  concepts?: {
    id: string;
    wikidata?: string;
    displayName: string;
    level: number;
    score: number;
  }[];
  topics?: {
    id: string;
    displayName: string;
    score: number;
    subfield?: {
      id: string;
      displayName: string;
    };
    field?: {
      id: string;
      displayName: string;
    };
    domain?: {
      id: string;
      displayName: string;
    };
  }[];
  keywords?: {
    id: string;
    displayName: string;
    score: number;
  }[];
  sustainableDevelopmentGoals?: {
    id: string;
    displayName: string;
    score: number;
  }[];
  grants?: {
    funder?: string;
    funderDisplayName?: string;
    awardId?: string;
  }[];
  apcList?: {
    value?: number;
    currency?: string;
    provenance?: string;
    valueUsd?: number;
  };
  apcPaid?: {
    value?: number;
    currency?: string;
    provenance?: string;
    valueUsd?: number;
  };
  hasFulltext?: boolean;
  citedByCount?: number;
  biblioVenue?: string;
  isRetracted?: boolean;
  isParatext?: boolean;
  type?: string;
  typeCrossref?: string;
  indexedIn?: string[];
  language?: string;
}

// Academic Search Union type for all provider data
export type AcademicSearchProviderData =
  | AcademicSearchSemanticScholarData
  | AcademicSearchGoogleScholarData
  | AcademicSearchPubMedData
  | AcademicSearchArxivData
  | AcademicSearchEricData
  | AcademicSearchEuropePmcData
  | AcademicSearchOpenAlexData;

// Academic Search Publication interface with optional provider data
export interface AcademicSearchPublication
  extends AcademicSearchBasePublication {
  providerData?: AcademicSearchProviderData;
}

// Academic Search Publication field type for field selection
export type AcademicSearchPublicationField =
  | "doi"
  | "title"
  | "url"
  | "providerURL"
  | "authors"
  | "date"
  | "year"
  | "totalCitations"
  | "totalReferences"
  | "abstract"
  | "pdfURL"
  | "provider"
  | "providerData";

export const AcademicSearchPublicationFieldValues: readonly AcademicSearchPublicationField[] =
  [
    "doi",
    "title",
    "url",
    "providerURL",
    "authors",
    "date",
    "year",
    "totalCitations",
    "totalReferences",
    "abstract",
    "pdfURL",
    "provider",
    "providerData",
  ] as const;

// Academic Search Provider error interface
export interface AcademicSearchProviderError {
  provider: string;
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

// Academic Search response interface
export interface AcademicSearchResponse {
  estimatedTotalResults: number;
  results: Partial<AcademicSearchPublication>[];
  errors?: AcademicSearchProviderError[];
}

// Academic Fetch result interface
export interface AcademicFetchResult extends AcademicSearchPublication {
  /** The ID that was used to fetch this publication */
  id: string;
  /** The provider that was detected/queried */
  detectedProvider: AcademicSearchProvider;
}

// Academic Fetch error interface
export interface AcademicFetchError {
  /** The ID that failed to fetch */
  id: string;
  /** Error message */
  error: string;
  /** Optional error code */
  code?: string;
}

// Academic Fetch response interface
export interface AcademicFetchResponse {
  /** Successfully fetched publications */
  results: AcademicFetchResult[];
  /** Errors for IDs that couldn't be fetched */
  errors?: AcademicFetchError[];
}

// Document Parse response interface
export interface ParseResponse {
  markdown: string;
  pageCount: number;
  creditCount: number;
  usedLLM: boolean;
}

// Document Ask response interface
export interface AskResponse {
  markdown: string;
  pageCount: number;
  creditCount: number;
}

// Document Extract response interface
export interface ExtractResponse {
  data: Record<string, unknown>;
  pageCount: number;
  creditCount: number;
}

// Temporary upload response interface
export interface TemporaryUploadResponse {
  key: string;
  url: string;
  exp: number;
}

// Document List Images response interface
export interface ListImagesResponse {
  images: string[];
  pageCount: number;
  creditCount: number;
  usedLLM: boolean;
}

// Invoice Parse response interface
export interface ParseInvoiceResponse {
  markdown: string;
  pageCount: number;
  creditCount: number;
}

// Invoice Ask response interface
export interface AskInvoiceResponse {
  markdown: string;
  pageCount: number;
  creditCount: number;
}

// Invoice Extract response interface
export interface ExtractInvoiceResponse {
  data: Record<string, unknown>;
  pageCount: number;
  creditCount: number;
}

// ID Parse response interface
export interface ParseIdResponse {
  markdown: string;
  pageCount: number;
  creditCount: number;
}

// ID Ask response interface
export interface AskIdResponse {
  markdown: string;
  pageCount: number;
  creditCount: number;
}

// ID Extract response interface
export interface ExtractIdResponse {
  data: Record<string, unknown>;
  pageCount: number;
  creditCount: number;
}
